import { FC, useState, useEffect } from 'react';
import {
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  useToast,
  Center,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Heading,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
} from '@chakra-ui/react';
import {
  MdSchool,
  MdCheckCircle,
  MdWarning,
  MdInfo,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { PageHeading, Panel, Table, AnimatedCard, containerVariants, cardVariants, TableRowActions } from '@bemap/ui';
import { formatDate, openUrl, useAuth } from '@bemap/utils';
import {
  getMyCertificates,
  requestCertificate,
  downloadCertificate,
  CourseCertificate,
} from '../../services/course-certificates-service';
import { getEnrolledCourses, CourseStudent } from '../../services/university-service';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const CourseCertificatesPage: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [certificates, setCertificates] = useState<CourseCertificate[]>([]);
  const [courses, setCourses] = useState<CourseStudent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [certificatesRes, coursesRes] = await Promise.all([
        getMyCertificates(),
        getEnrolledCourses(user?.id || 0),
      ]);

      setCertificates(certificatesRes.message || []);
      setCourses(coursesRes.message || []);
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('general.error_loading_data'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCertificate = async (course: CourseStudent) => {
    try {
      await requestCertificate(course.id);
      toast({
        title: t('general.success'),
        description: t('certificates.certificate_requested_successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await loadData();
      onClose();
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_requesting_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // const handleCheckCompletion = async (course: CourseStudent) => {
  //   setActionLoading(true);
  //   try {
  //     await checkCourseCompletion(course.id);
  //     toast({
  //       title: t('general.success'),
  //       description: t('certificates.course_completion_checked_successfully'),
  //       status: 'success',
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //     await loadData();
  //   } catch (error: any) {
  //     toast({
  //       title: t('general.error'),
  //       description: error.message || t('certificates.error_checking_completion'),
  //       status: 'error',
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   } finally {
  //     setActionLoading(false);
  //   }
  // };

  const handleDownloadCertificate = async (certificate: CourseCertificate) => {
    try {
      const { message: downloadUrl } = await downloadCertificate(certificate.id);
      openUrl(downloadUrl);
    } catch (error: any) {
      toast({
        title: t('general.error'),
        description: error.message || t('certificates.error_downloading_certificate'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting':
        return 'yellow';
      case 'generated':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'waiting':
        return <MdWarning />;
      case 'generated':
        return <MdCheckCircle />;
      case 'cancelled':
        return <MdInfo />;
      default:
        return <MdInfo />;
    }
  };

  const completedCourses = courses.filter(course => course.overallProgress === 100 && !course.certificateId);

  const certificatesCount = certificates.length;
  const generatedCertificates = certificates.filter(c => c.status === 'generated').length;
  const waitingCertificates = certificates.filter(c => c.status === 'waiting').length;

  const columns = [
    {
      header: t('certificates.course'),
      accessorKey: 'course.title',
      cell: ({ row }: any) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{row.original.course?.title}</Text>
          <Text fontSize="sm" color="gray.500">{row.original.course?.instructor}</Text>
        </VStack>
      ),
    },
    {
      header: t('certificates.status'),
      accessorKey: 'status',
      cell: ({ row }: any) => (
        <HStack spacing={2}>
          {getStatusIcon(row.original.status)}
          <Badge colorScheme={getStatusColor(row.original.status)}>
            {row.original.status}
          </Badge>
        </HStack>
      ),
    },
    {
      header: t('certificates.grade'),
      accessorKey: 'grade',
      cell: ({ row }: any) => (
        <Text>{row.original.grade ? `${row.original.grade}%` : 'N/A'}</Text>
      ),
    },
    {
      header: t('certificates.completion'),
      accessorKey: 'completionPercentage',
      cell: ({ row }: any) => (
        <Text>{row.original.completionPercentage}%</Text>
      ),
    },
    {
      header: t('certificates.issue_date'),
      accessorKey: 'issueDate',
      cell: ({ row }: any) => (
        <Text>{formatDate(row.original.issueDate)}</Text>
      ),
    },
    {
      header: t('certificates.actions'),
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <TableRowActions downloadAction={() => handleDownloadCertificate(row.original)} />
      ),
    },
  ];

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      w="100%"
      align="stretch"
      gap={6}
    >
      <PageHeading title={t('certificates.title')} />
      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <AnimatedCard variants={cardVariants}>
          <Stat>
            <StatLabel>{t('certificates.total_certificates')}</StatLabel>
            <StatNumber>{certificatesCount}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {t('certificates.all_your_certificates')}
            </StatHelpText>
          </Stat>
        </AnimatedCard>
        <AnimatedCard variants={cardVariants}>
          <Stat>
            <StatLabel>{t('certificates.generated')}</StatLabel>
            <StatNumber>{generatedCertificates}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              {t('certificates.ready_to_download')}
            </StatHelpText>
          </Stat>
        </AnimatedCard>
        <AnimatedCard variants={cardVariants}>
          <Stat>
            <StatLabel>{t('certificates.waiting')}</StatLabel>
            <StatNumber>{waitingCertificates}</StatNumber>
            <StatHelpText>
              <StatArrow type="decrease" />
              {t('certificates.pending_generation')}
            </StatHelpText>
          </Stat>
        </AnimatedCard>
      </SimpleGrid>

      {/* Request Certificate Section */}
      {completedCourses.length > 0 && (
        <AnimatedCard mb={6} variants={cardVariants}>
          <VStack spacing={4} align="stretch">
            <Heading size="md">{t('certificates.request_new_certificate')}</Heading>
            <Text color="gray.600">
              {t('certificates.completed_courses_eligible', { count: completedCourses.length })}
            </Text>
            <Button
              onClick={onOpen}
              colorScheme="blue"
              leftIcon={<MdSchool />}
            >
              {t('certificates.request_certificate')}
            </Button>
          </VStack>
        </AnimatedCard>
      )}

      <Panel>
        {/* Certificates Table */}
        {certificates.length > 0 ? (
          <Table
            data={certificates}
            columns={columns}
            tableId="client-certificates-table"
          />
        ) : (
          <Center py={10}>
            <VStack spacing={4}>
              <MdSchool size={48} color="gray.400" />
              <Text fontSize="lg" color="gray.500">
                {t('certificates.no_certificates_found')}
              </Text>
              <Text color="gray.400">
                {t('certificates.complete_courses_to_earn')}
              </Text>
            </VStack>
          </Center>
        )}
      </Panel>

      {/* Request Certificate Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('certificates.request_certificate_modal_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                {t('certificates.select_completed_course')}
              </Text>
              {completedCourses.map((course) => {
                const hasCertificate = certificates.some(c => c.courseId === course.id);
                return (
                  <AnimatedCard key={course.id} variant="outline" variants={cardVariants}>
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="medium">{course.course?.title}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {t('certificates.progress')}: {course.overallProgress || 0}%
                      </Text>
                      {hasCertificate ? (
                        <Badge colorScheme="green">{t('certificates.certificate_already_exists')}</Badge>
                      ) : (
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={() => handleRequestCertificate(course)}
                        >
                          {t('certificates.request_certificate_button')}
                        </Button>
                      )}
                    </VStack>
                  </AnimatedCard>
                );
              })}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionBox>
  );
};

export default CourseCertificatesPage; 